<section class="flex h-full flex-1 flex-col gap-4 rounded-lg bg-primary-50 p-4">
  @if (hasTransport) {
    <div class="flex flex-row items-center justify-between">
      <div class="flex items-center gap-2">
        <h3 class="text-primary-900">
          {{ "calculator.transport" | transloco }}
        </h3>
      </div>
      <p-button
        icon="pi pi-plus"
        [label]="'calculator.addTransport' | transloco"
        variant="outlined"
        (click)="addTransport()"
      />
    </div>
    <section
      class="grid grid-cols-[auto_auto_auto] items-center bg-white p-4 mdsm:grid-cols-[auto_auto_auto_auto_auto] lg:grid-cols-[auto_auto_auto_auto_auto_auto]"
    >
      <!-- Header -->
      <div
        class="hidden h-full w-full items-center py-3 text-paragraph-s-med text-greyscale-500 mdsm:flex"
      >
        {{ "tripDashboard.transport.type" | transloco }}
      </div>
      <div
        class="flex h-full w-full items-center p-1 text-paragraph-s-med text-greyscale-500 lg:p-3"
      >
        {{ "tripDashboard.transport.title" | transloco }}
      </div>

      <div
        class="flex h-full w-full items-center p-1 text-paragraph-s-med text-greyscale-500 lg:p-3"
      >
        {{ "tripDashboard.transport.journey" | transloco }}
      </div>
      <div
        class="hidden h-full w-full items-center justify-center p-1 text-paragraph-s-med text-greyscale-500 mdsm:flex lg:p-3"
      >
        {{ "tripDashboard.transport.mainTransport" | transloco }}
      </div>
      <div
        class="hidden h-full w-full items-center p-1 text-paragraph-s-med text-greyscale-500 lg:flex lg:p-3"
      >
        {{ "tripDashboard.transport.scheduledDays" | transloco }}
      </div>
      <div
        class="flex h-full w-full items-center p-1 text-paragraph-s-med text-greyscale-500 lg:p-3"
      ></div>
      <!-- rows -->
      @for (
        entry of tripService.sortedTransports();
        track entry.originalIndex
      ) {
        <!-- icon -->
        <div
          class="hidden h-full w-full items-center border-t border-greyscale-100 py-3 text-paragraph-med text-greyscale-500 mdsm:flex"
        >
          <lucide-icon [name]="getIcon(entry.item.type)" [size]="18" />
        </div>
        <!-- title -->
        <div
          class="flex h-full min-w-0 flex-1 items-center border-t border-greyscale-100 p-1 text-paragraph-med text-greyscale-700 lg:p-3"
        >
          <span class="truncate">
            {{
              entry.item.title ??
                entry.item.customEmissionFactor?.name ??
                (entry.item.type
                  ? ("categoryType." + entry.item.type | transloco)
                  : "")
            }}</span
          >
          <app-item-validation-badge
            class="ml-2"
            scope="transport"
            [index]="entry.originalIndex"
            (badgeClick)="onItemValidationBadgeClick($event)"
          />
        </div>

        <!-- journey -->
        <div
          class="flex h-full min-w-0 flex-1 items-center gap-2 border-t border-greyscale-100 p-1 text-paragraph-med text-greyscale-700 lg:p-3"
        >
          @if (entry.item.flightStops && entry.item.flightStops.length > 0) {
            <p>
              @for (airport of entry.item.flightStops; track $index) {
                {{ getFlightStopAirportCode(entry.item, $index) }}
                @if (airport.type !== "arrival") {
                  →
                }
              }
            </p>
          } @else if (entry.item.roadStops && entry.item.roadStops.length > 0) {
            <div
              class="flex min-w-0 flex-1 flex-col overflow-hidden leading-tight"
            >
              <span class="truncate">{{
                getRoadStopAddress(entry.item, 0)
              }}</span>
              <!-- <span class="text-sm leading-none">→</span> -->
              <span class="truncate">{{
                getDestinationRoadStopAddress(entry.item)
              }}</span>
            </div>
          } @else if (entry.item.distance && entry.item.distance > 0) {
            <p>
              {{ entry.item.distance * (entry.item.return ? 2 : 1) + " km" }}
            </p>
          } @else if (entry.item.duration && entry.item.duration > 0) {
            <p>
              @if (entry.item.type === "cruise") {
                {{
                  entry.item.duration +
                    " " +
                    (entry.item.duration > 1
                      ? ("travelBoards.days" | transloco)
                      : ("travelBoards.day" | transloco))
                }}
              } @else {
                {{
                  entry.item.duration * (entry.item.return ? 2 : 1)
                    | durationFormat
                }}
              }
            </p>
          } @else {
            <p class="text-paragraph-s-reg text-greyscale-500">
              {{
                "calculator.aiAnalysisResults.distanceOrDurationNotSpecified"
                  | transloco
              }}
            </p>
          }
          @if (shouldDisplayReturnBadge(entry.item)) {
            <span
              class="hidden rounded-xl bg-gray-100 p-1 px-2 text-paragraph-xs-reg mdsm:flex"
            >
              {{
                entry.item.return
                  ? ("calculator.return" | transloco)
                  : ("calculator.oneWay" | transloco)
              }}
            </span>
          }
        </div>

        <!-- colonne 4 -->
        <div
          class="hidden h-full min-w-0 flex-1 items-center justify-center border-t border-greyscale-100 p-1 text-paragraph-med text-greyscale-700 mdsm:flex lg:p-3"
        >
          <app-main-transport-checkbox [index]="entry.originalIndex" />
        </div>

        <!-- colonne 5 -->
        <div
          class="hidden h-full min-w-0 flex-1 flex-wrap items-center gap-2 border-t border-greyscale-100 p-1 text-paragraph-med text-greyscale-700 lg:flex lg:p-3"
        >
          @for (day of entry.item.scheduledDays; track $index) {
            <span
              class="rounded-xl bg-gray-100 p-1 px-2 text-paragraph-xs-reg"
              >{{ ("travelBoards.day" | transloco) + " " + day }}</span
            >
          }
        </div>

        <!-- colonne 6 (actions) -->
        <div
          class="flex h-full w-full items-center border-t border-greyscale-100 p-1 text-paragraph-med text-greyscale-700 lg:p-3"
        >
          <p-button
            icon="pi pi-ellipsis-v"
            (click)="op.toggle($event)"
            rounded
            variant="text"
            size="small"
          ></p-button>
          <p-popover #op>
            <div class="flex flex-col">
              <p-button
                icon="pi pi-pencil"
                [label]="'edit' | transloco"
                variant="text"
                (onClick)="editTransport(entry.originalIndex)"
              />
              <p-button
                icon="pi pi-trash"
                [label]="'delete' | transloco"
                variant="text"
                severity="danger"
                (onClick)="confirmDelete(entry.originalIndex)"
              />
            </div>
          </p-popover>
        </div>
      }
    </section>
  } @else {
    <app-prestation-empty-state
      [title]="'calculator.emptyState.transport.title' | transloco"
      [text]="'calculator.transportEmptyStateText' | transloco"
      [buttonLabel]="'calculator.addTransport' | transloco"
      [action]="addTransport"
    />
  }
</section>
